import { useEffect, useLayoutEffect, useRef } from "react";
import { CanvasInputError, toolError, validateInput, type InputSchema } from "./inputValidation";
export type { InputSchema } from "./inputValidation";

export interface NativeTool {
  name: string;
  description: string;
  inputSchema: InputSchema & { additionalProperties: false };
  annotations: { readOnlyHint: boolean };
  execute: (input: unknown, options: { signal: AbortSignal }) => Promise<unknown>;
}

export function describeTool(description: string, schema: InputSchema): string {
  description +=
    "\nEvery result includes a fresh page-local requestId for this invocation. It is not a backend trace or a mutation operationId. Retries retain operationId/arguments but receive a new requestId. Backend support IDs, when available, are separate. No scene content is logged.";
  if (!schema.examples?.length) return description;
  return `${description}\nJSON input examples (substitute live readToken/cursor/existing IDs; choose fresh IDs for additions and new requests):\n${schema.examples
    .map((example) => JSON.stringify(example))
    .join("\n")}`;
}

export interface NativeModelContext {
  registerTool: (tool: NativeTool, options: { signal: AbortSignal }) => Promise<void>;
}

interface ToolOptions<Input extends object> {
  name: string;
  description: string;
  inputSchema: InputSchema;
  enabled: boolean;
  readOnly?: boolean;
  handler: (input: Input, signal?: AbortSignal) => Promise<string>;
}

const unavailableDocuments = new WeakSet<Document>();

function nativeContext(): NativeModelContext | null {
  const context: unknown = Reflect.get(document, "modelContext");
  if (
    context &&
    typeof context === "object" &&
    "registerTool" in context &&
    typeof context.registerTool === "function"
  ) {
    return context as NativeModelContext;
  }
  return null;
}

/** Register only with the browser-owned API; never create a page-local substitute. */
export function useNativeWebMCP<Input extends object>(options: ToolOptions<Input>): void {
  const handler = useRef(options.handler);
  useLayoutEffect(() => {
    handler.current = options.handler;
  });
  const { enabled, name, description, readOnly = false } = options;
  // Schemas are inline literals in the caller. Avoid unregistering on unrelated renders.
  const schemaJson = JSON.stringify(options.inputSchema);
  useEffect(() => {
    if (!enabled) return;
    const context = nativeContext();
    if (!context) {
      if (!unavailableDocuments.has(document)) {
        unavailableDocuments.add(document);
        console.warn("[WebMCP] Native document.modelContext is unavailable; no tools registered.");
      }
      return;
    }
    const lifetime = new AbortController();
    const inputSchema: InputSchema = JSON.parse(schemaJson);
    const tool: NativeTool = {
      name,
      description: describeTool(description, inputSchema),
      inputSchema: { ...inputSchema, additionalProperties: false },
      annotations: { readOnlyHint: readOnly },
      execute: async (input, { signal }) => {
        const requestId = crypto.randomUUID();
        let combined: AbortSignal | undefined;
        try {
          if (lifetime.signal.aborted || signal.aborted) {
            throw new CanvasInputError("ABORTED", "This tool invocation is no longer active.");
          }
          validateInput(input, inputSchema);
          combined = AbortSignal.any([signal, lifetime.signal]);
          const result = await handler.current(input as Input, combined);
          if (combined.aborted) {
            throw new CanvasInputError("ABORTED", "This tool invocation is no longer active.");
          }
          const payload: unknown = JSON.parse(result);
          if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
            throw new Error("Invalid tool result");
          }
          return { ...payload, requestId };
        } catch (error) {
          return {
            ...toolError(
              combined?.aborted
                ? new CanvasInputError("ABORTED", "This tool invocation is no longer active.")
                : error,
            ),
            requestId,
          };
        }
      },
    };
    const register = async () => {
      try {
        await context.registerTool(tool, { signal: lifetime.signal });
      } catch (error) {
        if (!lifetime.signal.aborted) {
          console.error("[WebMCP] Native tool registration failed", {
            tool: name,
            errorType: error instanceof Error ? error.name : "UnknownError",
          });
        }
      }
    };
    void register();
    return () => lifetime.abort();
  }, [enabled, name, description, readOnly, schemaJson]);
}
